import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TTL_HOURS = 24;

/**
 * Map IANA timezone → headline bucket.
 *
 * Every browser exposes `Intl.DateTimeFormat().resolvedOptions().timeZone`
 * which is the user's IANA timezone (e.g. "Europe/Dublin"). This is
 * accurate enough for marketing-headline targeting (we don't need GDPR-
 * grade geo for a copy A/B test), works instantly on first paint, costs
 * nothing, and respects the visitor's actual location even on Emergent
 * preview where the proxy strips `CF-IPCountry`.
 */
const TIMEZONE_TO_BUCKET = {
  // Ireland
  'Europe/Dublin': 'IE',
  // UK + Crown Dependencies
  'Europe/London': 'UK',
  'Europe/Belfast': 'UK',
  'Europe/Guernsey': 'UK',
  'Europe/Isle_of_Man': 'UK',
  'Europe/Jersey': 'UK',
  // United States — every CONUS + AK/HI timezone routes to US
  'America/New_York': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Kentucky/Louisville': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Boise': 'US',
  'America/Anchorage': 'US',
  'America/Juneau': 'US',
  'America/Sitka': 'US',
  'America/Adak': 'US',
  'Pacific/Honolulu': 'US',
  // Canada → US bucket (close enough copy-wise; revisit when we localise CA)
  'America/Toronto': 'US',
  'America/Vancouver': 'US',
  'America/Edmonton': 'US',
  'America/Winnipeg': 'US',
  'America/Halifax': 'US',
  'America/St_Johns': 'US',
};

const bucketFromTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_TO_BUCKET[tz]) {
      return { bucket: TIMEZONE_TO_BUCKET[tz], country: tz.split('/')[1] || 'IE', source: 'tz' };
    }
    // Rest of Europe → UK-flavour generic copy (English-language fallback)
    if (tz && tz.startsWith('Europe/')) {
      return { bucket: 'UK', country: tz.split('/')[1] || 'EU', source: 'tz-eu' };
    }
    // Rest of Americas → US-flavour copy
    if (tz && (tz.startsWith('America/') || tz.startsWith('Pacific/'))) {
      return { bucket: 'US', country: tz.split('/')[1] || 'AM', source: 'tz-am' };
    }
  } catch {
    // Old browser, no Intl support — fall through to backend / default
  }
  return null;
};

/**
 * Decide which "country bucket" (IE / UK / US) the visitor falls into,
 * for geo-targeted marketing copy.
 *
 * Order of precedence:
 *   1. `?geo=IE|UK|US` URL query parameter (sticky via localStorage) —
 *      gives the team a way to preview each market.
 *   2. 24h localStorage cache from a previous detection.
 *   3. Backend `/api/geo/country` — reads Cloudflare / Vercel / Emergent
 *      edge headers off the actual request IP. Trusts the network the
 *      user is *browsing from* (which is what a VPN changes), NOT their
 *      OS timezone (which a VPN does NOT change).
 *   4. Browser IANA timezone → bucket map — kept as a fallback for the
 *      rare case where the backend call fails.
 *   5. Default `IE`.
 *
 * The previous ordering put timezone before backend, which broke VPN
 * users (someone in Dublin on a UK VPN kept getting the IE site because
 * their OS timezone stayed `Europe/Dublin`). Backend-first fixes that.
 */
export const useGeoBucket = () => {
  const [bucket, setBucket] = useState(null);
  const [country, setCountry] = useState(null);
  const [source, setSource] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // 1) URL override (sticky via localStorage so it persists across nav)
    try {
      const urlParam = new URLSearchParams(window.location.search).get('geo');
      const override = urlParam || localStorage.getItem('geo_override');
      if (override && ['IE', 'UK', 'US'].includes(override.toUpperCase())) {
        const ov = override.toUpperCase();
        if (urlParam) localStorage.setItem('geo_override', ov);
        setBucket(ov);
        setCountry(ov === 'UK' ? 'GB' : ov);
        setSource('override');
        return;
      }
    } catch {
      // ignore
    }

    // 2) Cache
    try {
      const cached = localStorage.getItem('geo_bucket_v1');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.t && Date.now() - parsed.t < TTL_HOURS * 3600 * 1000) {
          setBucket(parsed.bucket);
          setCountry(parsed.country);
          setSource(parsed.source || 'cache');
          return;
        }
      }
    } catch {
      // ignore
    }

    // 3) Backend IP-based detection (VPN-aware; timezone is not)
    axios
      .get(`${BACKEND_URL}/api/geo/country`)
      .then((r) => {
        if (cancelled) return;
        const b = r.data?.bucket;
        const c = r.data?.country;
        // Only accept the backend result if it actually resolved a country
        // (i.e. the edge header was present). An "IE" default with no
        // header info should NOT be preferred over the timezone guess —
        // fall through to the timezone step in that case.
        const wasResolved = r.data?.source && r.data.source !== 'default';
        if (b && c && wasResolved) {
          setBucket(b);
          setCountry(c);
          setSource('backend');
          try {
            localStorage.setItem(
              'geo_bucket_v1',
              JSON.stringify({ bucket: b, country: c, source: 'backend', t: Date.now() })
            );
          } catch {
            // ignore
          }
          return;
        }
        // Backend couldn't resolve — fall back to timezone.
        const tzGuess = bucketFromTimezone();
        if (tzGuess) {
          if (cancelled) return;
          setBucket(tzGuess.bucket);
          setCountry(tzGuess.country);
          setSource(tzGuess.source);
          try {
            localStorage.setItem(
              'geo_bucket_v1',
              JSON.stringify({ ...tzGuess, t: Date.now() })
            );
          } catch {
            // ignore
          }
          return;
        }
        // Absolute last resort — default IE.
        if (cancelled) return;
        setBucket('IE');
        setCountry('IE');
        setSource('default');
      })
      .catch(() => {
        if (cancelled) return;
        // Backend unreachable — fall back to timezone.
        const tzGuess = bucketFromTimezone();
        if (tzGuess) {
          setBucket(tzGuess.bucket);
          setCountry(tzGuess.country);
          setSource(tzGuess.source);
        } else {
          setBucket('IE');
          setCountry('IE');
          setSource('default');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { bucket, country, source };
};

export default useGeoBucket;
